import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { fehlertextAusEdgeFunction } from "@/lib/edgeFunktionFehler";
import {
  VERTRAEGE,
  WIDERRUF_PFADE,
  type ErklaerungBestaetigung,
  type ErklaerungEingabe,
} from "@/lib/vertragErklaerung";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

/**
 * Formular hinter „Vertrag widerrufen“ (Fußzeile, Sitzung „Aufräumen“, 14.10.2026). Ohne Anmeldung nutzbar. Die
 * Schaltfläche heißt wie vom Gesetz verlangt „Widerruf bestätigen“ (§ 356a BGB). Nach dem Absenden geht es auf die
 * Bestätigungsseite mit Datum und Uhrzeit des Eingangs; die Angaben kommen über den Verlauf mit, nicht über die
 * Adresse (sie gehören nicht in Server-Logs). Kündigen geht nur im Konto (Daniel, 15.10.2026).
 */
export function ErklaerungsFormular() {
  const navigate = useNavigate();

  const [vertrag, setVertrag] = useState("");
  const [details, setDetails] = useState("");
  const [kundennummer, setKundennummer] = useState("");
  const [vertragsdatum, setVertragsdatum] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [lockvogel, setLockvogel] = useState("");
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const anderer = vertrag === "Anderer Vertrag";

  const absenden = async (ev: FormEvent) => {
    ev.preventDefault();
    setFehler(null);
    if (!vertrag) return setFehler("Bitte wähle aus, welchen Vertrag du meinst.");
    if (anderer && !details.trim()) return setFehler("Bitte beschreibe kurz, welchen Vertrag du meinst.");

    const eingabe: ErklaerungEingabe = {
      art: "widerruf",
      name,
      email,
      vertrag,
      vertrag_details: details || undefined,
      kundennummer: kundennummer || undefined,
      vertragsdatum: vertragsdatum || undefined,
      website: lockvogel || undefined,
    };

    setSendet(true);
    try {
      const { data, error } = await supabase.functions.invoke("vertrag-erklaerung", { body: eingabe });
      if (error) throw new Error(await fehlertextAusEdgeFunction(error));
      if (!data?.ok) throw new Error(data?.error ?? "Das hat nicht geklappt.");
      const bestaetigung: ErklaerungBestaetigung = {
        eingabe,
        eingegangen_am: data.eingegangen_am,
        referenz: data.referenz ?? "",
        mail_gesendet: data.mail_gesendet === true,
      };
      navigate(WIDERRUF_PFADE.bestaetigung, { state: bestaetigung });
    } catch (e) {
      setFehler(
        `${e instanceof Error ? e.message : "Das hat nicht geklappt."} Du kannst uns auch an info@musikonzept.de schreiben.`,
      );
      setSendet(false);
    }
  };

  const feld = "space-y-2";

  return (
    <form onSubmit={absenden} className="space-y-6">
      <div className={feld}>
        <Label htmlFor="vertrag">Welchen Vertrag möchtest du widerrufen? *</Label>
        <Select value={vertrag} onValueChange={setVertrag}>
          <SelectTrigger id="vertrag" aria-required="true">
            <SelectValue placeholder="Bitte auswählen" />
          </SelectTrigger>
          <SelectContent>
            {VERTRAEGE.map((v) => (
              <SelectItem key={v} value={v}>
                {v}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className={feld}>
        <Label htmlFor="details">Angaben zum Vertrag {anderer ? "*" : "(wahlweise)"}</Label>
        <Textarea
          id="details"
          value={details}
          onChange={(e) => setDetails(e.target.value)}
          maxLength={2000}
          rows={3}
          required={anderer}
          placeholder="z. B. welcher Termin oder welcher Kurs"
        />
      </div>

      <div className={feld}>
        <Label htmlFor="kundennummer">Kunden- oder Rechnungsnummer (wahlweise)</Label>
        <Input id="kundennummer" value={kundennummer} onChange={(e) => setKundennummer(e.target.value)} maxLength={100} />
      </div>

      <div className={feld}>
        <Label htmlFor="vertragsdatum">Bestellt am (wahlweise)</Label>
        <Input
          id="vertragsdatum"
          type="date"
          value={vertragsdatum}
          onChange={(e) => setVertragsdatum(e.target.value)}
          className="max-w-[220px]"
        />
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        <div className={feld}>
          <Label htmlFor="name">Vor- und Nachname *</Label>
          <Input id="name" value={name} onChange={(e) => setName(e.target.value)} maxLength={200} required autoComplete="name" />
        </div>
        <div className={feld}>
          <Label htmlFor="email">E-Mail-Adresse *</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            maxLength={320}
            required
            autoComplete="email"
          />
          <p className="text-xs text-muted-foreground">
            An diese Adresse schicken wir die Bestätigung. Am besten die Adresse, mit der du bei uns angemeldet bist.
          </p>
        </div>
      </div>

      {/* Lockvogel für Programme: für Menschen unsichtbar und ohne Tab-Stopp */}
      <div aria-hidden="true" className="absolute -left-[9999px] h-0 w-0 overflow-hidden">
        <label htmlFor="website">Webseite</label>
        <input id="website" tabIndex={-1} autoComplete="off" value={lockvogel} onChange={(e) => setLockvogel(e.target.value)} />
      </div>

      {fehler && (
        <p role="alert" className="rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {fehler}
        </p>
      )}

      <div className="space-y-3">
        <Button
          type="submit"
          size="lg"
          disabled={sendet}
          className="min-h-[48px] rounded-full bg-[#227277] px-8 font-bold text-white hover:bg-[#1D2934]"
        >
          {sendet && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Widerruf bestätigen
        </Button>
        <p className="text-xs text-muted-foreground">
          Mit dem Klick geht deine Widerrufserklärung bei uns ein. Wir verarbeiten deine
          Angaben nur, um sie zu bearbeiten und zu belegen (
          <Link to="/datenschutz" className="underline">
            Datenschutz
          </Link>
          ).
        </p>
      </div>
    </form>
  );
}
